import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { Sparkles, Coins, Loader2, RefreshCw, Download, AlertTriangle, Briefcase } from "lucide-react";

const STYLES = [
  { id: "minimal",  label: "Minimal",  desc: "Clean, flat, lots of space" },
  { id: "wordmark", label: "Wordmark", desc: "Custom lettering only" },
  { id: "emblem",   label: "Emblem",   desc: "Crest / shield wrap" },
  { id: "monogram", label: "Monogram", desc: "Initials interlocked" },
  { id: "mascot",   label: "Mascot",   desc: "Character illustration" },
  { id: "abstract", label: "Abstract", desc: "Geometric symbol" },
];

const PALETTES = [
  { id: "auto",  label: "Auto",     swatch: "from-zinc-700 to-zinc-500" },
  { id: "mono",  label: "Mono",     swatch: "from-zinc-900 to-zinc-200" },
  { id: "gold",  label: "Gold",     swatch: "from-black to-amber-400" },
  { id: "blue",  label: "Navy/Blue", swatch: "from-blue-900 to-blue-400" },
  { id: "warm",  label: "Warm",     swatch: "from-amber-700 to-rose-300" },
  { id: "cool",  label: "Cool",     swatch: "from-emerald-800 to-emerald-300" },
  { id: "bold",  label: "Bold",     swatch: "from-red-600 to-yellow-400" },
];

const QUALITIES = [
  { id: "draft",  label: "Draft",  desc: "Quick exploration" },
  { id: "pro",    label: "Pro",    desc: "Recommended balance" },
  { id: "cinema", label: "Cinema", desc: "Highest fidelity" },
];

export default function LogoGeneratorPage() {
  const { user, loading } = useAuth();
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [style, setStyle] = useState("minimal");
  const [palette, setPalette] = useState("auto");
  const [customColor1, setCustomColor1] = useState("");   // hex like "#D4AF37"
  const [customColor2, setCustomColor2] = useState("");   // hex like "#080808"
  const [brief, setBrief] = useState("");
  const [quality, setQuality] = useState("pro");
  const [credits, setCredits] = useState({ draft: 2, pro: 3, cinema: 6 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]); // recent logos rendered this session

  useEffect(() => {
    if (!user) return;
    // Live estimate for the chip strip
    (async () => {
      try {
        const [d, p, c] = await Promise.all([
          api.get("/credits/estimate?feature=logo&quality=draft"),
          api.get("/credits/estimate?feature=logo&quality=pro"),
          api.get("/credits/estimate?feature=logo&quality=cinema"),
        ]);
        setCredits({ draft: d.data.credits, pro: p.data.credits, cinema: c.data.credits });
      } catch { /* keep defaults */ }
    })();
  }, [user]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/generate/logo" replace />;

  const onGenerate = async () => {
    if (!businessName.trim()) {
      setError("Add a business or brand name to start.");
      return;
    }
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/generate/logo", {
        business_name: businessName.trim(),
        industry: industry.trim(),
        style, palette,
        custom_colors: [customColor1, customColor2].filter(Boolean),
        brief: brief.trim(),
        aspect: "1:1",
        quality,
      });
      setHistory((h) => [data, ...h].slice(0, 12));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardShell>
      <div className="mb-8 flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-3">
            <Briefcase className="w-3.5 h-3.5 text-[#D4AF37]" />
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37]">Logo Generator · Business Studio</p>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Brand-grade logos in seconds.</h1>
          <p className="text-[#A0A0A0] mt-4">
            Iterate on multiple directions cheaply. Try 4–6 ideas in one session — Draft renders cost just {credits.draft} credits each.
            Logos are rendered as square 1:1 marks ready for social, web, and print.
          </p>
        </div>

        {/* Credit chip strip — transparency before generating */}
        <div className="bg-[#0F0F0F] border border-[#2A2A2A] px-5 py-4">
          <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Cost per logo</p>
          <div className="grid grid-cols-3 gap-2 min-w-[280px]">
            {QUALITIES.map((q) => (
              <div key={q.id}
                   data-testid={`logo-credit-chip-${q.id}`}
                   className={`px-3 py-2 border text-center ${quality === q.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A]"}`}>
                <p className="text-[9px] uppercase tracking-wider text-[#A0A0A0]">{q.label}</p>
                <p className="font-bold text-[#F4F4F0] inline-flex items-center gap-1 mt-0.5">
                  <Coins className="w-3 h-3 text-[#D4AF37]" /> {credits[q.id]}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* ─── Inputs ─── */}
        <div className="lg:col-span-2 space-y-5">
          <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-5 md:p-6 space-y-4">
            <Field label="Business / Brand name *" value={businessName} onChange={setBusinessName} placeholder="YBM Studios" testId="logo-business-name" />
            <Field label="Industry (optional)" value={industry} onChange={setIndustry} placeholder="creative tech · law firm · streetwear" testId="logo-industry" />

            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Logo style</p>
              <div className="grid grid-cols-2 gap-2" data-testid="logo-style-grid">
                {STYLES.map((s) => (
                  <button key={s.id} onClick={() => setStyle(s.id)} data-testid={`logo-style-${s.id}`}
                          className={`text-left px-3 py-2 border transition-colors ${style === s.id ? "border-[#D4AF37] bg-[#D4AF37]/5 text-[#D4AF37]" : "border-[#2A2A2A] hover:border-[#D4AF37]/40 text-[#F4F4F0]"}`}>
                    <p className="text-xs font-bold">{s.label}</p>
                    <p className="text-[10px] text-[#A0A0A0] mt-0.5">{s.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Palette</p>
              <div className="grid grid-cols-4 gap-2" data-testid="logo-palette-grid">
                {PALETTES.map((p) => (
                  <button key={p.id} onClick={() => setPalette(p.id)} data-testid={`logo-palette-${p.id}`}
                          className={`px-2 py-2 border transition-colors text-center ${palette === p.id ? "border-[#D4AF37]" : "border-[#2A2A2A] hover:border-[#D4AF37]/40"}`}>
                    <div className={`h-4 w-full mb-1 bg-gradient-to-r ${p.swatch}`} />
                    <p className={`text-[10px] uppercase tracking-wider ${palette === p.id ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>{p.label}</p>
                  </button>
                ))}
              </div>

              {/* Custom brand colors — keep the preset chips above, add exact
                  hex pickers for users with locked brand colors. */}
              <div className="mt-4 border border-[#1F1F1F] bg-[#0A0A0A] p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Custom brand colors (optional)</p>
                  {(customColor1 || customColor2) && (
                    <button
                      type="button"
                      onClick={() => { setCustomColor1(""); setCustomColor2(""); }}
                      data-testid="logo-clear-custom-colors"
                      className="text-[10px] uppercase tracking-wider text-[#5A5A5A] hover:text-[#D4AF37]"
                    >Clear</button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <ColorField label="Primary"   color={customColor1} setColor={setCustomColor1} testId="logo-color-1" />
                  <ColorField label="Secondary" color={customColor2} setColor={setCustomColor2} testId="logo-color-2" />
                </div>
                {(customColor1 || customColor2) && (
                  <p className="text-[10px] text-[#D4AF37] mt-2" data-testid="logo-custom-colors-note">
                    Custom colors will override the {palette === "auto" ? "Auto" : palette} preset.
                  </p>
                )}
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Brand brief (optional)</p>
              <textarea
                value={brief} rows={3} maxLength={600}
                onChange={(e) => setBrief(e.target.value)}
                data-testid="logo-brief"
                placeholder="e.g. 'Premium streetwear brand for late-night creatives. Confident, edgy, but luxe.'"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm text-[#F4F4F0]"
              />
              <p className="text-[10px] text-[#5A5A5A] mt-1">{brief.length} / 600</p>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Quality</p>
              <div className="grid grid-cols-3 gap-2">
                {QUALITIES.map((q) => (
                  <button key={q.id} onClick={() => setQuality(q.id)} data-testid={`logo-quality-${q.id}`}
                          className={`px-3 py-2 border text-left transition-colors ${quality === q.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] hover:border-[#D4AF37]/40"}`}>
                    <p className={`text-xs font-bold ${quality === q.id ? "text-[#D4AF37]" : "text-[#F4F4F0]"}`}>{q.label}</p>
                    <p className="text-[10px] text-[#A0A0A0]">{q.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="px-3 py-2 border border-red-500/40 bg-red-500/10 text-red-300 text-xs flex items-center gap-2" data-testid="logo-error">
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
              </div>
            )}

            <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3 flex items-center justify-between" data-testid="logo-cost-banner">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Estimated cost</p>
                <p className="font-bold text-lg text-[#F4F4F0] inline-flex items-center gap-1">
                  <Coins className="w-4 h-4 text-[#D4AF37]" /> {credits[quality]} credits
                </p>
              </div>
              <button
                onClick={onGenerate}
                disabled={busy || !businessName.trim()}
                data-testid="logo-generate-button"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#080808] font-bold px-6 py-3 text-sm hover:bg-[#F0C84B] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate Logo</>}
              </button>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-wider text-[#5A5A5A] leading-relaxed">
            Tip — iterate first at Draft, then re-run your favorite direction at Pro or Cinema for the final mark. Failed renders auto-refund.
          </div>
        </div>

        {/* ─── Preview gallery ─── */}
        <div className="lg:col-span-3">
          <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-3">This session</p>
          {history.length === 0 ? (
            <div className="border border-dashed border-[#2A2A2A] bg-[#0A0A0A] aspect-[4/3] flex flex-col items-center justify-center text-center px-6" data-testid="logo-empty-state">
              <Briefcase className="w-10 h-10 text-[#2A2A2A] mb-4" />
              <p className="text-[#F4F4F0] font-display text-lg mb-1">Your renders show up here.</p>
              <p className="text-[#A0A0A0] text-sm max-w-md">Enter your business name, pick a style and palette, and hit Generate. Run multiple variations — Draft renders cost just {credits.draft} credits each.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="logo-history">
              {history.map((item, i) => (
                <figure key={item.render_id || i} className="bg-[#0F0F0F] border border-[#2A2A2A] hover:border-[#D4AF37]/50 transition-colors overflow-hidden group" data-testid={`logo-result-${i}`}>
                  <div className="aspect-square bg-white">
                    <img
                      src={`${BACKEND_URL}${item.preview_url || `/api/renders/${item.render_id}/file`}`}
                      alt="Logo render"
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <figcaption className="px-4 py-3 border-t border-[#1F1F1F] flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#F4F4F0] font-bold">{businessName || "Untitled"}</p>
                      <p className="text-[10px] text-[#A0A0A0] uppercase tracking-wider mt-0.5">{style} · {palette}</p>
                    </div>
                    <a
                      href={`${BACKEND_URL}/api/renders/${item.render_id}/download`}
                      data-testid={`logo-download-${i}`}
                      title="Download"
                      className="text-[#A0A0A0] hover:text-[#D4AF37] transition-colors p-1.5"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </figcaption>
                </figure>
              ))}
              <button
                onClick={onGenerate}
                disabled={busy || !businessName.trim()}
                data-testid="logo-regenerate"
                className="bg-[#0F0F0F] border border-dashed border-[#2A2A2A] hover:border-[#D4AF37] aspect-square flex flex-col items-center justify-center text-[#A0A0A0] hover:text-[#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <RefreshCw className="w-6 h-6" />}
                <p className="text-[10px] uppercase tracking-wider mt-2">Try another</p>
              </button>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1.5">{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm text-[#F4F4F0] placeholder-[#5A5A5A]"
      />
    </label>
  );
}

function ColorField({ label, color, setColor, testId }) {
  const isHex = /^#?[0-9a-fA-F]{6}$/.test(color || "");
  const normalised = color ? (color.startsWith("#") ? color : `#${color}`) : "";
  return (
    <div className="flex items-center gap-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <span
          className="w-9 h-9 border border-[#2A2A2A] inline-block shrink-0"
          style={{ background: isHex ? normalised : "repeating-linear-gradient(45deg, #1F1F1F, #1F1F1F 4px, #0A0A0A 4px, #0A0A0A 8px)" }}
          aria-hidden
        />
        <input
          type="color"
          value={isHex ? normalised : "#D4AF37"}
          onChange={(e) => setColor(e.target.value)}
          data-testid={`${testId}-picker`}
          className="sr-only"
        />
        <span className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">{label}</span>
      </label>
      <input
        type="text"
        value={color}
        onChange={(e) => setColor(e.target.value)}
        placeholder="#D4AF37"
        maxLength={7}
        data-testid={`${testId}-hex`}
        className="flex-1 min-w-0 bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-2 py-1.5 text-xs text-[#F4F4F0] font-mono placeholder-[#5A5A5A]"
      />
    </div>
  );
}

